import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FiMessageSquare, FiSend, FiUser } from 'react-icons/fi';

const API_BASE = `${process.env.REACT_APP_API_URL}/api`;

function MessagesView({ userId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [receiverId, setReceiverId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    // Poll for new messages every 3 seconds
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, [userId, selectedBooking]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const url = selectedBooking
        ? `${API_BASE}/messages/?user_id=${userId}&booking_id=${selectedBooking}`
        : `${API_BASE}/messages/?user_id=${userId}`;
      
      const response = await axios.get(url);
      if (response.data.success) {
        setMessages(response.data.data);
        setError(null);
      }
    } catch (err) {
      setError('Failed to load messages');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !receiverId) return;

    try {
      const response = await axios.post(`${API_BASE}/messages/?user_id=${userId}`, {
        receiver_id: receiverId,
        message_text: newMessage,
        booking_id: selectedBooking
      });

      if (response.data.success) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (err) {
      alert('Failed to send message');
      console.error(err);
    }
  };

  if (loading && messages.length === 0) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 border-3 border-primary/30 border-t-primary rounded-full animate-spin"></div>
      <span className="ml-3 text-gray-500">Loading messages...</span>
    </div>
  );
  if (error) return <div className="text-center py-12 text-red-500">{error}</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <FiMessageSquare className="w-5 h-5 text-accent" />
          Messages
        </h2>
        <p className="text-sm text-gray-500 mt-1">Chat with your lawyer</p>
      </div>

      <div className="bg-white rounded-2xl shadow-card overflow-hidden">
        {/* Messages List */}
        <div className="h-96 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <FiMessageSquare className="w-10 h-10 mb-3 opacity-30" />
              <p className="font-medium">No messages yet.</p>
              <p className="text-sm">Start a conversation!</p>
            </div>
          ) : (
            messages.map(msg => (
              <div
                key={msg.id}
                className={`flex ${msg.is_sent_by_me ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                  msg.is_sent_by_me
                    ? 'bg-primary text-white rounded-br-md'
                    : 'bg-white shadow-sm border border-gray-100 rounded-bl-md'
                }`}>
                  <p className={`text-xs font-semibold mb-1 ${msg.is_sent_by_me ? 'text-white/70' : 'text-primary'}`}>
                    {msg.sender_name}
                  </p>
                  <p className={`text-sm ${msg.is_sent_by_me ? 'text-white' : 'text-gray-700'}`}>
                    {msg.message_text}
                  </p>
                  <span className={`text-xs mt-1 block ${msg.is_sent_by_me ? 'text-white/50' : 'text-gray-400'}`}>
                    {new Date(msg.created_at).toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Form */}
        <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-100 bg-white">
          <div className="flex items-end gap-3">
            <div className="flex-shrink-0">
              <div className="relative">
                <FiUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="number"
                  placeholder="Receiver ID"
                  value={receiverId || ''}
                  onChange={(e) => setReceiverId(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-28 pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                />
              </div>
            </div>
            <div className="flex-1">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                rows="2"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={!newMessage.trim() || !receiverId}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl text-sm font-medium transition-colors shadow-md"
            >
              <FiSend className="w-4 h-4" />
              Send
            </button>
          </div>
        </form>
      </div>
    </motion.div>
  );
}

export default MessagesView;
